import * as stylex from "@stylexjs/stylex";
import { springConfigs } from "charts/lib/motion";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { useMedia, useRTL } from "hooks";
import React, { useContext } from "react";
import { breakpoints } from "../../materials/consts.stylex";
import { themeVars } from "../../materials/theme.stylex";
import { typography } from "../../materials/typography.stylex";

const styles = stylex.create({
  root: {
    padding: "16px",
    backgroundColor: "white",
    // FIXME: define global shadow styles
    boxShadow: "0 15px 40px -10px rgba(0, 0, 0, 0.28)",
  },
  topLink: {
    display: "block",
    color: "black",
    ":hover": {
      opacity: 0.5,
    },
  },
  progressTrack: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "4px",
    backgroundColor: themeVars.backgroundLight,
  },
  progressBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "4px",
    transformOrigin: "left",
    backgroundColor: themeVars.backgroundMain,
  },
  itemBase: {
    display: "block",
    color: themeVars.textColor,
    position: "relative",
    ":hover": {
      opacity: 0.5,
    },
  },
  itemSidebar: {
    backgroundColor: themeVars.backgroundLight,
    padding: "8px 16px",
  },
  itemTop: {
    backgroundColor: "none",
    padding: "0px",
  },
  itemActive: {
    fontWeight: "bold",
  },
  itemInactive: {
    fontWeight: "normal",
  },
  marginLeftZero: {
    marginLeft: 0,
  },
  marginRightZero: {
    marginRight: 0,
  },
  marginLeft1em: {
    marginLeft: "1em",
  },
  marginRight1em: {
    marginRight: "1em",
  },
  beforeLtr: {
    "::before": {
      content: '"–"',
      display: "block",
      position: "absolute",
      opacity: 0.8,
      left: "-1em",
    },
  },
  beforeRtl: {
    "::before": {
      content: '"–"',
      display: "block",
      position: "absolute",
      opacity: 0.8,
      right: "-1em",
    },
  },
});

export const TOCContext = React.createContext<{
  ids: string[];
  addId: (x: string) => void;
  removeId: (x: string) => void;
}>({
  ids: [],
  addId: () => {},
  removeId: () => {},
});

export const TOC = () => {
  const [headings, setHeadings] = React.useState<HTMLCollectionOf<HTMLHeadingElement> | null>(null);
  const [visible, setVisible] = React.useState(false);

  const { ids } = useContext(TOCContext);

  React.useEffect(() => {
    const $headings = document.getElementsByTagName("h2");
    setHeadings($headings);
  }, []);

  const goTop = React.useCallback(() => {
    window.scrollTo(0, 0);
  }, []);

  const screenMDown = !useMedia(breakpoints.m);

  const { scrollYProgress, scrollY } = useScroll();
  const scaleX = useTransform(scrollYProgress, [0, 0.95], [0, 1]);
  const opacity = useTransform(scrollY, [200, 400], [0, 1]);
  const posY = useTransform(scrollY, [200, 400], [100, 0]);

  if (screenMDown) {
    return null;
  }

  return (
    <>
      <motion.div
        initial={false}
        style={{ opacity, translateY: posY }}
        {...stylex.props(styles.root)}
        onMouseEnter={() => {
          setVisible(true);
        }}
        onMouseLeave={() => {
          setVisible(false);
        }}
      >
        <AnimatePresence>
          <motion.div
            animate={{
              height: visible ? "auto" : "0px",
              opacity: visible ? 1 : 0,
            }}
            transition={springConfigs.gentle}
            style={{
              overflow: "hidden",
              width: "100%",
              background: "white",
              boxSizing: "border-box",
              position: "absolute",
              bottom: "3em",
              left: 0,
              zIndex: 2,
            }}
          >
            <Items headings={headings} ids={ids} variant="sidebar" />
          </motion.div>
        </AnimatePresence>

        <a
          href="#"
          onClick={() => {
            goTop();
          }}
          {...stylex.props(styles.topLink)}
        >
          Top
        </a>
        <div {...stylex.props(styles.progressTrack)} />
        <motion.div {...stylex.props(styles.progressBar)} style={{ scaleX }} />
      </motion.div>
    </>
  );
};

export const TOCStatic = () => {
  const [headings, setHeadings] = React.useState<HTMLCollectionOf<HTMLHeadingElement> | null>(null);
  React.useEffect(() => {
    const $headings = document.getElementsByTagName("h2");
    setHeadings($headings);
  }, []);
  return (
    <div>
      <Items headings={headings} ids={[]} variant="top" />
    </div>
  );
};

function Items({
  headings,
  ids,
  variant,
}: {
  headings: HTMLCollectionOf<HTMLHeadingElement> | null;
  ids: string[];
  variant: "sidebar" | "top";
}) {
  const isRTL = useRTL();

  return (
    <>
      {[...Object.values(headings || {})]
        .filter((d) => !!d.id)
        .map((d) => {
          const active = ids.includes(d.id);
          const sidebar = variant === "sidebar";
          const marginStyle = isRTL
            ? sidebar
              ? styles.marginRightZero
              : styles.marginRight1em
            : sidebar
              ? styles.marginLeftZero
              : styles.marginLeft1em;
          return (
            <div key={d.id}>
              <a
                href={`#${d.id}`}
                {...stylex.props(
                  sidebar ? typography.body2Alt : typography.lead1,
                  styles.itemBase,
                  sidebar ? styles.itemSidebar : styles.itemTop,
                  marginStyle,
                  active ? styles.itemActive : styles.itemInactive,
                  isRTL ? styles.beforeRtl : styles.beforeLtr,
                )}
              >
                {d.textContent}
              </a>
            </div>
          );
        })}
    </>
  );
}
